import { BadgeCheck, BadgeX, FileText } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FilePreview } from "@/components/FilePreview";
import { API } from "@/lib/api";

function ProofDialog({ proof, onClose }) {
  return (
    <Dialog open={!!proof} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-2xl" data-testid="proof-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Comprobante de pago</DialogTitle>
        </DialogHeader>
        {proof && (
          <>
            <a
              href={`${API}/files/${proof.file_id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid="proof-open-new-tab"
            >
              Abrir "{proof.original_filename}" en pestaña nueva ↗
            </a>
            <FilePreview file={proof} heightClass="h-[60vh]" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RejectPaymentDialog({ busy, onReject }) {
  const [reason, setReason] = useState("");
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50" disabled={busy} data-testid="reject-payment-btn">
          <BadgeX className="h-4 w-4 mr-2" strokeWidth={1.5} /> Rechazar pago
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">¿Rechazar el comprobante de pago?</AlertDialogTitle>
          <AlertDialogDescription>
            El paciente será notificado para comunicarse contigo por WhatsApp y subir un nuevo
            comprobante. Puedes indicar el motivo (opcional).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej.: El monto no coincide / la captura no es legible..."
          data-testid="reject-reason-textarea"
        />
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="reject-cancel-btn">Cancelar</AlertDialogCancel>
          <AlertDialogAction
            className="rounded-full bg-red-600 hover:bg-red-700"
            onClick={() => onReject(reason)}
            data-testid="reject-confirm-btn"
          >
            Sí, rechazar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function PaymentVerificationCard({ req, onAccept, onReject }) {
  const [busy, setBusy] = useState(false);
  const [proofOpen, setProofOpen] = useState(null);
  const proofs = (req.files || []).filter((f) => f.kind === "payment_proof");

  if (req.status === "payment_rejected") {
    return (
      <div className="border border-red-200 rounded-2xl bg-red-50 p-5" data-testid="payment-rejected-pro-banner">
        <p className="font-heading font-semibold text-red-800 mb-1">Comprobante rechazado</p>
        <p className="text-sm text-red-700">
          Rechazaste el comprobante de pago{req.payment_rejection_reason ? ` (${req.payment_rejection_reason})` : ""}.
          El paciente fue notificado para comunicarse contigo y subir uno nuevo.
        </p>
      </div>
    );
  }

  if (req.payment_verification !== "pending") return null;

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-2 border-amber-300 rounded-2xl bg-amber-50 p-5" data-testid="payment-verification-card">
      <p className="font-heading font-semibold text-amber-900 mb-1">Verifica el comprobante de pago</p>
      <p className="text-sm text-amber-800 mb-3">
        El paciente pagó con tu liga de Stripe y subió su comprobante. Revísalo antes de preparar la receta.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {proofs.map((f) => (
          <button
            key={f.file_id}
            onClick={() => setProofOpen(f)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border border-amber-400 bg-white hover:bg-amber-100 transition-colors duration-200"
            data-testid={`proof-view-${f.file_id}`}
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> Ver {f.original_filename.slice(0, 28)}
          </button>
        ))}
        {proofs.length === 0 && <p className="text-xs text-amber-800">(sin comprobante adjunto)</p>}
      </div>
      <ProofDialog proof={proofOpen} onClose={() => setProofOpen(null)} />
      <div className="flex flex-wrap gap-3">
        <Button
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={busy}
          onClick={() => act(onAccept)}
          data-testid="accept-payment-btn"
        >
          <BadgeCheck className="h-4 w-4 mr-2" strokeWidth={1.5} /> Aceptar pago
        </Button>
        <RejectPaymentDialog busy={busy} onReject={(reason) => act(() => onReject(reason))} />
      </div>
    </div>
  );
}
